import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ChevronRight, 
  ChevronDown, 
  ArrowLeft, 
  ArrowRight, 
  Sparkles, 
  CheckCircle2, 
  HelpCircle, 
  ShieldCheck, 
  Zap, 
  TrendingUp, 
  Target, 
  Users, 
  Award, 
  BarChart3, 
  Compass,
  Building2
} from 'lucide-react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import LeadForm from '@/components/LeadForm';
import AnimatedCounter from '@/components/AnimatedCounter';
import { getServiceByPath } from '@/data/servicesData';
import { getContextualServiceDetails } from '@/data/serviceDetailsContent';
import { useCustomImages } from '@/context/ImageContext';

const ServiceDetailPage = () => {
  const { getImage } = useCustomImages();
  const location = useLocation();
  const navigate = useNavigate();
  const [openFaq, setOpenFaq] = useState(0);

  const serviceInfo = getServiceByPath(location.pathname);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  const pageTitle = serviceInfo ? serviceInfo.itemTitle : 'Digital Solutions';
  const categoryTitle = serviceInfo ? serviceInfo.categoryTitle : 'Services';
  const groupName = serviceInfo ? serviceInfo.groupName : null;

  // Retrieve service-specific contextual data
  const details = getContextualServiceDetails(location.pathname, pageTitle, categoryTitle, groupName);

  const heroImage = getImage(location.pathname, details.heroImage);
  const faqList = details.faqs;

  return (
    <>
      <Helmet>
        <title>{`${pageTitle} Services - ${categoryTitle} | Stellars Estates`}</title>
        <meta 
          name="description" 
          content={`${details.intro.slice(0, 155)}...`} 
        />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={`${details.h1} | Stellars Estates`} />
        <meta property="og:description" content={`${details.intro.slice(0, 155)}...`} />
        <meta property="og:image" content="https://i.postimg.cc/3NNd39Q1/social-sharing-image-stallars-estates.jpg" />
        <meta property="og:image:secure_url" content="https://i.postimg.cc/3NNd39Q1/social-sharing-image-stallars-estates.jpg" />
        <meta property="og:image:alt" content={`${pageTitle} - Stellars Estates`} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content="https://i.postimg.cc/3NNd39Q1/social-sharing-image-stallars-estates.jpg" />
      </Helmet>

      <div className="min-h-screen bg-[#181F32]">
        <Header />

        {/* 1. HERO SECTION WITH RELEVANT IMAGE & 2. H1 HEADLINE */}
        <section className="bg-[#181F32] text-white pt-12 pb-16 sm:py-20 lg:py-24 relative overflow-hidden border-b border-white/10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            {/* Breadcrumb */}
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex flex-wrap items-center gap-2 text-xs sm:text-sm text-white/70 mb-6"
            >
              <Link to="/" className="hover:text-[#F3453F] transition-colors">Home</Link>
              <ChevronRight size={14} className="text-white/40" />
              <Link to="/services" className="hover:text-[#F3453F] transition-colors">Services</Link>
              <ChevronRight size={14} className="text-white/40" />
              <span className="text-white/80">{categoryTitle}</span>
              {groupName && (
                <>
                  <ChevronRight size={14} className="text-white/40" />
                  <span className="text-white/80">{groupName}</span>
                </>
              )}
              <ChevronRight size={14} className="text-white/40" />
              <span className="text-[#F3453F] font-semibold">{pageTitle}</span>
            </motion.div>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center">
              {/* Left Column: Text Content */}
              <div className="lg:col-span-7 text-left">
                <motion.div
                  initial={{ opacity: 0, y: 15 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 }}
                  className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-[#F3453F]/15 border border-[#F3453F]/30 text-[#F3453F] text-xs font-semibold mb-4"
                >
                  <Sparkles size={14} />
                  <span>{details.badge}</span>
                </motion.div>

                {/* H1 Headline */}
                <motion.h1
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.15 }}
                  className="text-3xl sm:text-4xl lg:text-5xl font-extrabold mb-6 font-heading text-white tracking-tight leading-tight"
                >
                  {details.h1}
                </motion.h1>

                {/* Service Introduction */}
                <motion.p
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                  className="text-base sm:text-lg text-white/85 font-sans leading-relaxed mb-8 max-w-2xl"
                >
                  {details.intro}
                </motion.p>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.25 }}
                  className="flex flex-wrap items-center gap-4"
                >
                  <button
                    onClick={() => {
                      const element = document.getElementById('contact-form');
                      if (element) element.scrollIntoView({ behavior: 'smooth' });
                    }}
                    className="btn-glass btn-glass-primary px-6 py-3 text-sm font-semibold flex items-center gap-2"
                  >
                    <span>Request Strategy Audit</span>
                    <ArrowRight size={16} />
                  </button>
                  <button
                    onClick={() => navigate('/services')}
                    className="btn-glass btn-glass-secondary px-5 py-3 text-sm font-medium flex items-center gap-2"
                  >
                    <ArrowLeft size={16} />
                    <span>View All Services</span>
                  </button>
                </motion.div>
              </div>

              {/* Right Column: Relevant Hero Image Card */}
              <div className="lg:col-span-5">
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.2 }}
                  className="relative rounded-2xl overflow-hidden border border-white/20 glass-panel shadow-2xl group"
                >
                  <div className="aspect-[4/3] relative overflow-hidden bg-[#181F32]">
                    <img
                      src={heroImage}
                      alt={`${pageTitle} strategy banner`}
                      referrerPolicy="no-referrer"
                      className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-700 opacity-80"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#181F32] via-[#181F32]/40 to-transparent" />
                  </div>

                  {/* Overlaid Benchmarks */}
                  <div className="absolute bottom-4 left-4 right-4 p-4 rounded-xl bg-[#111726]/90 backdrop-blur-md border border-white/15 flex items-center justify-between text-left">
                    <div>
                      <div className="text-xs uppercase font-extrabold text-[#F3453F] tracking-wider mb-0.5">
                        {details.benchmarks[0]?.label || 'Primary Benchmark'}
                      </div>
                      <div className="text-lg font-extrabold text-white font-heading">
                        <AnimatedCounter value={details.benchmarks[0]?.value || 'Top Performance'} />
                      </div>
                    </div>
                    <div className="p-2.5 rounded-lg bg-[#F3453F]/20 text-[#F3453F]">
                      <TrendingUp size={22} />
                    </div>
                  </div>
                </motion.div>
              </div>
            </div>
          </div>
        </section>

        {/* 4. WHAT IS THIS SERVICE */}
        <section className="py-16 sm:py-20 bg-[#181F32]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-left">
            <div className="max-w-3xl mb-12">
              <span className="glass-badge mb-3">Service Overview</span>
              <h2 className="text-2xl sm:text-4xl font-extrabold text-white font-heading mb-4">
                What is <span className="text-[#F3453F]">{pageTitle}</span>?
              </h2>
              <p className="text-white/80 text-sm sm:text-base leading-relaxed font-sans">
                {details.whatIsText}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {details.pillars.map((pillar, idx) => (
                <div key={idx} className="glass-panel p-6 rounded-2xl border border-white/10">
                  <div className="p-3 rounded-xl bg-[#F3453F]/15 text-[#F3453F] w-fit mb-4">
                    {idx === 0 ? <Target size={24} /> : idx === 1 ? <Zap size={24} /> : <BarChart3 size={24} />}
                  </div>
                  <h3 className="text-lg font-bold text-white font-heading mb-2">{pillar.title}</h3>
                  <p className="text-xs sm:text-sm text-white/75 font-sans leading-relaxed">
                    {pillar.desc}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* 5. HOW IT WORKS */}
        <section className="py-16 sm:py-20 bg-[#141A29] border-y border-white/10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-left">
            <div className="text-center max-w-2xl mx-auto mb-14">
              <span className="glass-badge mb-3">Structured Methodology</span>
              <h2 className="text-2xl sm:text-4xl font-extrabold text-white font-heading mb-3">
                How <span className="text-[#F3453F]">{pageTitle}</span> Works
              </h2>
              <p className="text-white/80 text-xs sm:text-sm font-sans">
                Our step-by-step execution roadmap ensures flawless campaign delivery and measurable commercial impact.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {details.howItWorks.map((item, idx) => (
                <div key={idx} className="glass-panel p-6 rounded-2xl border border-white/10 relative group">
                  <div className="text-3xl font-extrabold text-[#F3453F] font-heading mb-3 opacity-90">
                    {item.step}
                  </div>
                  <h3 className="text-lg font-bold text-white font-heading mb-2">
                    {item.title}
                  </h3>
                  <p className="text-xs sm:text-sm text-white/75 font-sans leading-relaxed">
                    {item.desc}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* 6. BENEFITS & 7. WHY BUSINESSES NEED IT */}
        <section className="py-16 sm:py-20 bg-[#181F32]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-left">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
              
              {/* Why Businesses Need It */}
              <div className="lg:col-span-5">
                <span className="glass-badge mb-3">Strategic Necessity</span>
                <h2 className="text-2xl sm:text-4xl font-extrabold text-white font-heading mb-4 leading-tight">
                  Why Businesses Need <span className="text-[#F3453F]">{pageTitle}</span>
                </h2>
                <p className="text-white/80 text-sm sm:text-base leading-relaxed font-sans mb-6">
                  {details.whyNeedIt}
                </p>
                <div className="space-y-3 mb-6">
                  {details.whyPoints.map((point, idx) => (
                    <div key={idx} className="flex items-start gap-3">
                      <CheckCircle2 size={18} className="text-[#F3453F] shrink-0 mt-0.5" />
                      <span className="text-xs sm:text-sm text-white/90 font-medium">{point}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Key Benefits Grid */}
              <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-4">
                {details.benefits.map((benefit, idx) => (
                  <div key={idx} className="glass-panel-white p-5 rounded-2xl text-left border border-gray-200">
                    <div className="w-8 h-8 rounded-lg bg-[#F3453F]/10 text-[#F3453F] flex items-center justify-center mb-3">
                      <ShieldCheck size={18} />
                    </div>
                    <h3 className="text-base font-bold text-[#181F32] font-heading mb-1">
                      {benefit.title}
                    </h3>
                    <p className="text-xs text-[#181F32]/80 font-sans leading-relaxed font-medium">
                      {benefit.desc}
                    </p>
                  </div>
                ))}
              </div>

            </div>
          </div>
        </section>

        {/* 8. WHO NEEDS IT */}
        <section className="py-16 sm:py-20 bg-[#141A29] border-t border-white/10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-left">
            <div className="max-w-2xl mb-12">
              <span className="glass-badge mb-3">Target Profile</span>
              <h2 className="text-2xl sm:text-4xl font-extrabold text-white font-heading mb-3">
                Who Needs <span className="text-[#F3453F]">{pageTitle}</span>?
              </h2>
              <p className="text-white/80 text-xs sm:text-sm font-sans">
                Tailored execution frameworks designed specifically for businesses striving for predictable growth.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {details.whoNeedsIt.map((audience, idx) => (
                <div key={idx} className="glass-panel p-6 rounded-2xl border border-white/10">
                  <div className="p-3 rounded-xl bg-[#F3453F]/15 text-[#F3453F] w-fit mb-4">
                    {idx === 0 ? <Building2 size={22} /> : idx === 1 ? <TrendingUp size={22} /> : idx === 2 ? <Users size={22} /> : <Compass size={22} />}
                  </div>
                  <h3 className="text-base font-bold text-white font-heading mb-2">
                    {audience.title}
                  </h3>
                  <p className="text-xs text-white/75 font-sans leading-relaxed">
                    {audience.desc}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* 9. STELLARS ESTATES USP & BUSINESS OUTCOMES */}
        <section className="py-16 sm:py-20 bg-[#181F32]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-left">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
              
              {/* Stellars Estates USP */}
              <div className="lg:col-span-6">
                <span className="glass-badge mb-3">Why Us</span>
                <h2 className="text-2xl sm:text-4xl font-extrabold text-white font-heading mb-6">
                  The Stellars Estates <span className="text-[#F3453F]">Advantage</span>
                </h2>
                
                <div className="space-y-4">
                  {details.usps.map((usp, idx) => (
                    <div key={idx} className="glass-panel p-4 rounded-xl border border-white/10 flex items-start gap-4">
                      <div className="p-2.5 rounded-lg bg-[#F3453F]/20 text-[#F3453F] shrink-0 mt-0.5">
                        <Award size={18} />
                      </div>
                      <div>
                        <h3 className="text-sm font-bold text-white font-heading">{usp.title}</h3>
                        <p className="text-xs text-white/75 font-sans mt-0.5 leading-relaxed">{usp.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Business Outcomes */}
              <div className="lg:col-span-6 glass-panel-hero p-8 rounded-2xl border border-white/15">
                <span className="glass-badge mb-3">Target Benchmarks</span>
                <h2 className="text-2xl sm:text-3xl font-extrabold text-white font-heading mb-4">
                  Measurable <span className="text-[#F3453F]">Outcomes</span>
                </h2>
                <p className="text-white/80 text-xs sm:text-sm font-sans mb-6">
                  Key metrics we consistently achieve when deploying {pageTitle}:
                </p>

                <div className="grid grid-cols-2 gap-4 text-center sm:text-left">
                  {details.benchmarks.map((bm, idx) => (
                    <div key={idx} className="p-4 rounded-xl bg-white/5 border border-white/10">
                      <div className="text-2xl sm:text-3xl font-extrabold text-[#F3453F] font-heading">
                        <AnimatedCounter value={bm.value} />
                      </div>
                      <div className="text-xs text-white/80 font-medium mt-1">{bm.label}</div>
                    </div>
                  ))}
                </div>
              </div>

            </div>
          </div>
        </section>

        {/* 12. FAQ SECTION */}
        <section className="py-16 sm:py-20 bg-[#141A29] border-t border-white/10">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-left">
            <div className="text-center mb-12">
              <span className="glass-badge mb-3">Clear Answers</span>
              <h2 className="text-2xl sm:text-4xl font-extrabold text-white font-heading mb-3">
                Frequently Asked <span className="text-[#F3453F]">Questions</span>
              </h2>
              <p className="text-white/80 text-xs sm:text-sm font-sans">
                Everything you need to know about {pageTitle} with Stellars Estates.
              </p>
            </div>

            <div className="space-y-4">
              {faqList.map((faq, idx) => {
                const isOpen = openFaq === idx;
                return (
                  <div 
                    key={idx} 
                    className="glass-panel border border-white/10 rounded-2xl overflow-hidden transition-all"
                  >
                    <button
                      onClick={() => setOpenFaq(isOpen ? -1 : idx)}
                      className="w-full text-left p-5 sm:p-6 flex items-center justify-between gap-4 text-white hover:text-[#F3453F] transition-colors"
                    >
                      <span className="text-sm sm:text-base font-bold font-heading flex items-center gap-2">
                        <HelpCircle size={18} className="text-[#F3453F] shrink-0" />
                        {faq.q}
                      </span>
                      <ChevronDown 
                        size={18} 
                        className={`text-white/60 shrink-0 transition-transform duration-300 ${isOpen ? 'rotate-180 text-[#F3453F]' : ''}`} 
                      />
                    </button>

                    <AnimatePresence>
                      {isOpen && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          exit={{ opacity: 0, height: 0 }}
                          transition={{ duration: 0.25 }}
                          className="px-5 sm:px-6 pb-6 pt-1 text-xs sm:text-sm text-white/80 font-sans leading-relaxed border-t border-white/5"
                        >
                          {faq.a}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* 13. CTA & LEAD FORM */}
        <div id="contact-form">
          <LeadForm prefilledService={`${categoryTitle} - ${pageTitle}`} />
        </div>

        <Footer />
      </div>
    </>
  );
};

export default ServiceDetailPage;
